// Polyfills the reviver's third argument (`context.source`, the raw JSON
// token text) on engines that don't ship it, so `parseReadPlanTimesAsBigInt`
// can re-parse nanosecond integers at full precision.
import "core-js/actual/json/parse";

import { type RepresentationStorageFormat } from "@/shared/domain/topics";

/**
 * Wire shapes for `POST v2/topics/id/{topic_id}/read-plan`, mirroring the
 * Python contract in `packages/roboto/src/roboto/experimental/topics/`
 * (`operations.py` for the request, `record.py` for its representation
 * selector, `read_plan.py` for the response).
 *
 * The plan resolves a read of one topic (time window, field projection,
 * stored variant per field) into per-partition scan tasks that name their
 * backing files by `fs_node_id`, which `GET v1/files/{fs_node_id}/signed-url`
 * accepts. The plan carries no topic data; `executeReadPlan` fetches and
 * decodes the files it names.
 */

/**
 * The only `plan_version` that `resolveReadPlan` accepts, and the version
 * whose plan the types in this module describe.
 */
export const READ_PLAN_VERSION = 1;

/**
 * Addresses a schema field, and the subtree under it, by exactly one form.
 * `{ path: [] }` addresses the schema root.
 */
export type FieldAddress =
  | { path: string[]; field_id?: never }
  | { field_id: string; path?: never };

/**
 * Selects which stored variant of a field to read. The read takes only a
 * variant that meets every criterion set; a criterion left unset accepts any
 * value.
 *
 * When a selector with any criterion set matches no stored variant of a
 * projected field in some partition the plan reads, the service refuses the
 * whole read. Under a selector with no criteria set, a field with no stored
 * variant is left out of the plan.
 */
export interface RepresentationSelector {
  /** Required storage container. */
  storage_format?: RepresentationStorageFormat;
  /**
   * Required content encoding (e.g. `"jpeg"`), compared case-sensitively. A
   * variant with no content format does not meet it.
   */
  content_format?: string;
  /**
   * Required transformations. `[]` requires the untransformed original. A
   * non-empty list requires every entry, in any order, and the variant may
   * carry others: a bare kind (`"encode"`) matches any transformation of that
   * kind, and a full descriptor (`"encode:jpeg"`) matches only that
   * descriptor. The service refuses an entry whose kind it does not recognize.
   */
  transformations?: string[];
}

/**
 * Applies a selector to one field subtree in place of the preference's
 * `default`.
 */
export interface RepresentationOverride {
  field: FieldAddress;
  selector: RepresentationSelector;
}

/**
 * Selects which stored variant of each field to read, per subtree. `default`
 * governs every field no override covers; where several overrides cover a
 * field, the one addressing the deepest subtree wins.
 *
 * Where several stored variants meet a field's selector, the service picks
 * one in each partition by rules that may change between releases, so one
 * read can take different variants from different partitions. A read that
 * needs the same kind of variant from every partition, such as JPEG-encoded
 * images for display, sets criteria that only that kind meets.
 */
export interface RepresentationPreference {
  /** Omitted matches any variant. */
  default?: RepresentationSelector;
  overrides?: RepresentationOverride[];
}

export interface ReadPlanRequest {
  /**
   * Inclusive window lower bound, absolute Unix-epoch nanoseconds. May be
   * omitted only when the request names a `file_id`, `dataset_id` or
   * `device_id`; it then defaults to the earliest time of the topic's data
   * that the request's restrictions select, across every timeline source.
   */
  start_time?: bigint;
  /**
   * Inclusive window upper bound, absolute Unix-epoch nanoseconds. May be
   * omitted on the same terms as `start_time`, defaulting to the latest time
   * of that data.
   */
  end_time?: bigint;
  /**
   * Field subtrees to project; omitted projects every field. The service
   * refuses an empty list.
   */
  fields_include?: FieldAddress[];
  /**
   * Field subtrees to drop from the projection; omitted drops none.
   * `{ path: [] }` drops nothing. The service refuses an empty list, and a
   * list that leaves no field to project.
   */
  fields_exclude?: FieldAddress[];
  /** Which stored variant of each field to read; omitted accepts any. */
  prefer?: RepresentationPreference;
  /**
   * Schema to read under, by id. Set at most one of `schema_id` and
   * `schema_checksum`. With neither, the read uses the one schema the
   * in-window partitions carry, and the service refuses a window whose
   * partitions carry several.
   */
  schema_id?: string;
  /** Schema to read under, by checksum. See `schema_id`. */
  schema_checksum?: string;
  /**
   * Timeline source that each partition's `timestamp` and `time_offset_ns`
   * come from, by id. Set at most one of
   * `timeline_source_id` and `timeline_source_name`. With neither, the read
   * uses the source the schema flags as default, else its only source; the
   * service refuses a schema with several sources and none flagged.
   */
  timeline_source_id?: string;
  /** Timeline source to read under, by name. See `timeline_source_id`. */
  timeline_source_name?: string;
  /** Limits the read to the files recorded in this session. */
  session_id?: string;
  /** Limits the read to this file. */
  file_id?: string;
  /** Limits the read to this dataset's files. */
  dataset_id?: string;
  /**
   * Limits the read to this device's files: a file belongs to the device it
   * names, or, when it names none, to the device its dataset names.
   */
  device_id?: string;
}

export interface ReadPlanSchemaRef {
  schema_id: string;
  checksum: string;
}

/** A schema field named by both its id and its path components. */
export interface ReadPlanFieldRef {
  field_id: string;
  path: string[];
}

/**
 * Where a partition's row timestamps come from, by `kind`:
 * - "schema_field": the schema field `field` names. `unit` is the field's
 *   stored unit when the schema declares one; without one, values that do not
 *   carry their own unit are nanoseconds.
 * - "message_log_time" or "message_publish_time": the message envelope's log
 *   or publish time, always nanoseconds. `field` and `unit` are null.
 */
export interface ReadPlanTimestamp {
  kind: string;
  field?: ReadPlanFieldRef | null;
  unit?: string | null;
}

/**
 * The fields a read projects decoded rows to, in exactly one of two forms:
 * every field in the schema (`all`), or the listed `fields`.
 */
export type ReadPlanProjection =
  | { all: true; fields?: never }
  | { all?: never; fields: ReadPlanFieldRef[] };

export interface ReadPlanObjectRef {
  /** Identifier of the backing file; stable, so a consumer can cache by it. */
  fs_node_id: string;
  size_bytes?: number | null;
}

/** One file to open, with the format needed to interpret it. */
export interface ReadPlanScanTask {
  /** The field subtree this task covers; absent covers the whole schema. */
  subtree?: ReadPlanFieldRef | null;
  /**
   * Where two scan tasks' subtrees contain the same field, the read takes it
   * from the higher-precedence one.
   */
  precedence: number;
  format: RepresentationStorageFormat;
  /**
   * Transformations applied to produce this variant, in order; empty or
   * absent on the file as recorded.
   */
  transformations?: string[];
  object: ReadPlanObjectRef;
  /**
   * Name of the topic whose records this task reads from its file. A file
   * can hold several topics (an MCAP file does when its channels carry
   * different topic names), so a reader selects the topic's records by this
   * name. Absent when the plan does not name the topic; a reader then reads
   * the file as holding a single topic.
   */
  topic_name?: string | null;
}

/** Everything needed to fetch and interpret one in-window partition. */
export interface ReadPlanPartition {
  topic_part_id: string;
  /** Added to each decoded row timestamp; constant across the partition. */
  time_offset_ns: bigint;
  /**
   * The window this partition's rows are selected in: inclusive, absolute
   * Unix-epoch nanoseconds. A reader keeps only the rows whose timestamps,
   * once `time_offset_ns` is added, fall inside it. It is the only time filter
   * a read applies.
   *
   * It is the requested window intersected with the part of the partition's
   * file the read is scoped to, so it can be narrower than
   * `ReadPlan.window`: a read scoped to a session narrows it when the session
   * holds the file over only part of the file's time span. The narrowing is
   * stated per file, so every partition backed by one file carries the same
   * window; `data_range` separates the partitions packed into one file.
   */
  window: ReadPlanWindow;
  timestamp: ReadPlanTimestamp;
  /**
   * The slice of the file this partition owns, as `[start, end]`, or null
   * (or absent) for the whole file. `start` is the first owned position and
   * `end` is one past the last, like `Array.prototype.slice`.
   *
   * A slice appears when one file packs several partitions' data (a LeRobot
   * v3 data file, for example). Each of those partitions carries its own
   * `time_offset_ns` and may cover the same instants as the partitions
   * beside it in the file, so time alone cannot separate its rows from
   * theirs: a reader keeps only the rows at the slice's positions, before
   * the window test. A slice is a range of stored rows counted from 0, and a
   * transformed representation serves it only when it holds the same
   * content at the same positions.
   */
  data_range?: [number, number] | null;
  /**
   * The files to read for this partition; empty when the partition has no
   * readable data.
   *
   * A partition may have several scan tasks, each covering a subtree of the
   * schema or the whole schema; the read takes each field from the
   * highest-precedence scan task whose subtree contains it.
   */
  scan_tasks: ReadPlanScanTask[];
  /**
   * The file this partition's data was recorded in. A scan task can read a
   * transformed representation of that file, which is stored as a different
   * file, so a scan task's `object` does not always name the recording a
   * partition's rows came from; this id does.
   */
  fs_node_id: string;
}

/** A read's inclusive time bounds, absolute Unix-epoch nanoseconds. */
export interface ReadPlanWindow {
  start: bigint;
  end: bigint;
}

export interface ReadPlan {
  plan_version: number;
  topic_id: string;
  /**
   * The window the caller asked to read, which the plan was resolved over. A
   * reader selects rows by each partition's `window` alone, which can be
   * narrower than this one.
   *
   * Null only when the request omitted a bound and its restrictions select
   * no timestamped data for the topic, or the bound it gave lies past the
   * other end of that data; the plan then has no partitions.
   */
  window: ReadPlanWindow | null;
  /**
   * The schema the plan reads under. Null when no partition of the topic
   * lies in the window within the request's restrictions, and the plan then
   * has no partitions. A plan that names a schema may also have no
   * partitions.
   */
  schema: ReadPlanSchemaRef | null;
  projection: ReadPlanProjection;
  /**
   * Ordered by when each file's data begins. Partitions backed by the same
   * file are contiguous, ordered by where their `data_range` slice starts in
   * that file, so they arrive in the order their data sits in it.
   */
  partitions: ReadPlanPartition[];
}

/**
 * JSON keys in a `ReadPlan` whose values are nanosecond-epoch integers (or
 * nanosecond offsets) that can exceed `Number.MAX_SAFE_INTEGER`. They stay
 * out of the `parseTimestampsAsBigInt` allowlist, which applies to every
 * response it parses, because keys as common as `start` and `end` would turn
 * unrelated numbers into bigints there.
 */
const READ_PLAN_BIGINT_KEYS = new Set<string>([
  "start",
  "end",
  "time_offset_ns",
]);

/**
 * `JSON.parse` reviver for read-plan responses, coercing the keys above to
 * `bigint` from the raw token text (`context.source`).
 */
export function parseReadPlanTimesAsBigInt(
  key: string,
  value: unknown,
  context?: { source: string },
) {
  if (
    READ_PLAN_BIGINT_KEYS.has(key) &&
    context !== undefined &&
    typeof value === "number"
  ) {
    try {
      return BigInt(context.source);
    } catch {
      // `BigInt` throws on a non-integer token such as `1.5`; the number
      // parsed from it is returned instead.
      return value;
    }
  }
  return value;
}
